import { Field, Form, Formik } from 'formik'
import type { ReactNode } from 'react'
import React from 'react'

import { LeavingGuard } from '@components/utils/LeavingGuard'
import {
  Checkbox,
  Fieldset,
  Input,
  SaveShortcutListener,
  Subsection,
  Switch,
} from '@fourthwall/components'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'

import type { SettingsFormValues } from '../../AppsTiktokShop.types'
import { settingsFormValidationSchema } from '../../consts'
import { SyncProducts, type SyncProductsProps } from '../SyncProducts/SyncProducts'

export type SettingsFormProps = {
  initialValues: SettingsFormValues
  isLoading: boolean
  isDisabled: boolean
  errorBanners: ReactNode
  syncProducts: SyncProductsProps
  onSubmit: (values: SettingsFormValues) => void
}

export const SettingsForm = ({
  initialValues,
  errorBanners,
  isLoading,
  isDisabled,
  syncProducts,
  onSubmit,
}: SettingsFormProps) => {
  return (
    <Formik<SettingsFormValues>
      enableReinitialize
      initialValues={initialValues}
      validationSchema={settingsFormValidationSchema}
      onSubmit={onSubmit}
    >
      {({ values, dirty, isSubmitting, resetForm, isValid, submitForm }) => (
        <Form>
          <Subsection>
            {errorBanners}

            <Fieldset.Row>
              <Fieldset.Item>
                <Field
                  component={Switch}
                  name="salesEnabled"
                  label="Enable TikTok Shop sales"
                  description="Turning this on will allow fans to purchase your Fourthwall products directly from your TikTok Shop."
                  disabled={isDisabled || isLoading}
                />
              </Fieldset.Item>
            </Fieldset.Row>
          </Subsection>
          {values.salesEnabled && <SyncProducts {...syncProducts} />}
          {values.salesEnabled && (
            <Subsection title="Product pricing on TikTok Shop">
              <div>
                We recommend adding a 15% increase to your product prices. This covers both TikTok
                Shop's 6% transaction fee and allows you to offer free shipping for US-based orders
                from your TikTok shop. This is a best practice to maximize sales.
              </div>
              <Fieldset.Row>
                <Fieldset.Item>
                  <Field
                    component={Checkbox}
                    name="priceIncreaseEnabled"
                    label="Increase price of my products on TikTok Shop"
                    disabled={isDisabled || isLoading}
                  />
                </Fieldset.Item>
              </Fieldset.Row>
              {values.priceIncreaseEnabled && (
                <div style={{ marginTop: 16, width: 170 }}>
                  <Fieldset.Row inner>
                    <Fieldset.Item>
                      <Field
                        component={Input}
                        type="number"
                        name="priceIncrease"
                        label="Price increase"
                        append="%"
                        hideSpinner
                        disabled={isDisabled || isLoading}
                      />
                    </Fieldset.Item>
                  </Fieldset.Row>
                </div>
              )}
            </Subsection>
          )}

          <SaveShortcutListener disabled={!dirty} onSave={submitForm} />
          <ConfirmBar
            isOpen={dirty || isSubmitting}
            isLoading={isLoading || isSubmitting}
            onConfirm={submitForm}
            onCancel={resetForm}
          />
          <LeavingGuard when={dirty} isValid={isValid} onSave={submitForm} />
        </Form>
      )}
    </Formik>
  )
}
